import React, { useState, useEffect, memo } from 'react';
import { GameState, Position, Letter } from '../types/game';
import { canSelectPosition } from '../utils/gameLogic';
import { IS_DEBUG } from '../utils/env';
import LetterTile from './LetterTile';

// Memoized overlay tile component to prevent remounts
// Use custom comparison to ensure props are truly equal before re-rendering
const FallingOverlayTile = memo<{
  overlayData: {
    key: string;
    tile: Letter;
    fallDistance: number;
    initialYOffset: number;
    finalTop: number;
    finalLeft: number;
  };
}>(({ overlayData }) => (
  <div
    style={{
      position: 'absolute',
      top: `${overlayData.finalTop}px`,
      left: `${overlayData.finalLeft}px`,
      width: '60px',
      height: '60px',
      zIndex: 1000,
      pointerEvents: 'none',
      // CRITICAL: Optimize for smooth animations
      willChange: 'transform',
      backfaceVisibility: 'hidden',
      WebkitBackfaceVisibility: 'hidden',
      // Force GPU acceleration - use transform3d for better performance
      transform: 'translate3d(0, 0, 0)',
    }}
  >
    <LetterTile
      letter={overlayData.tile}
      isSelected={false}
      isInPath={false}
      isAvailable={false}
      onClick={() => {}}
      isRemoving={false}
      isShifting={true}
      isHinted={false}
      isHintedNext={false}
      fallDistance={overlayData.fallDistance}
      initialYOffset={overlayData.initialYOffset}
    />
  </div>
), (prev, next) => {
  // Prevent re-render if all props are the same
  // Return true means "skip re-render" (props are equal)
  return (
    prev.overlayData.key === next.overlayData.key &&
    prev.overlayData.fallDistance === next.overlayData.fallDistance &&
    prev.overlayData.initialYOffset === next.overlayData.initialYOffset &&
    prev.overlayData.finalTop === next.overlayData.finalTop &&
    prev.overlayData.finalLeft === next.overlayData.finalLeft &&
    prev.overlayData.tile.char === next.overlayData.tile.char &&
    prev.overlayData.tile.position.row === next.overlayData.tile.position.row &&
    prev.overlayData.tile.position.col === next.overlayData.tile.position.col
  );
});
FallingOverlayTile.displayName = 'FallingOverlayTile';

interface GameGridProps {
  gameState: GameState;
  onLetterClick: (position: Position) => void;
  isGravityActive?: boolean;
  isShrinking?: boolean;
  previousGrid?: Letter[][];
  selectedPathForAnimation?: Position[];
  isValidatingWord?: boolean;
  wordValidationResult?: 'valid' | 'invalid' | null;
}

const GameGrid: React.FC<GameGridProps> = React.memo(({ 
  gameState, 
  onLetterClick, 
  isGravityActive = false, 
  isShrinking = false,
  previousGrid,
  selectedPathForAnimation = [],
  isValidatingWord = false,
  wordValidationResult = null
}) => {
  const { grid, selectedPath } = gameState;
  
  // OPTIMIZE: Convert selectedPath to Set for O(1) lookups during render
  // This prevents O(n) array.some() calls for every tile
  const selectedPathSet = React.useMemo(() => {
    const set = new Set<string>();
    selectedPath.forEach(pos => set.add(`${pos.row}-${pos.col}`));
    return set;
  }, [selectedPath]);
  
  // Animation state
  const [animationPhase, setAnimationPhase] = useState<'idle' | 'removing' | 'shifting' | 'complete'>('idle');
  const [removingTiles, setRemovingTiles] = useState<Set<string>>(new Set());
  const [tilePositions, setTilePositions] = useState<Map<string, { oldRow: number; newRow: number; col: number }>>(new Map());
  
  // OPTIMIZE: Build reverse lookup map for origin positions (oldRow -> position)
  // This allows O(1) lookup instead of O(n) iteration during render
  const originPositionsMapRef = React.useRef<Map<string, { oldRow: number; newRow: number; col: number }>>(new Map());
  
  // Store overlay tiles in a ref to prevent recreating overlays when grid updates
  const overlayTilesRef = React.useRef<Map<string, Letter>>(new Map());
  // Store overlay data in refs to create stable elements
  const fallingOverlaysDataRef = React.useRef<Array<{
    key: string;
    tile: Letter;
    oldRow: number;
    newRow: number;
    col: number;
    fallDistance: number;
    initialYOffset: number;
    finalTop: number;
    finalLeft: number;
  }>>([]);
  const fallingOverlaysKeyRef = React.useRef<string>('');

  // Detect when gravity animation should start - use ref to prevent re-triggering
  const animationStartedRef = React.useRef(false);
  // Track when explosion started so falling can be timed correctly
  const explosionStartTimeRef = React.useRef<number | null>(null);
  // Track unique key for explosion to force remount
  const explosionKeyRef = React.useRef<string | null>(null);
  // Track if calculation should continue (for cancellation)
  const shouldContinueCalculationRef = React.useRef(true);
  
  useEffect(() => {
    performance.mark('gamegrid-useeffect-triggered');
    
    // Start explosion animation IMMEDIATELY when isGravityActive is true
    // Don't wait for previousGrid - we can use selectedPathForAnimation to start explosions
    if (isGravityActive && !animationStartedRef.current && animationPhase === 'idle') {
      // Start animation immediately using selectedPathForAnimation
      if (selectedPathForAnimation && selectedPathForAnimation.length > 0) {
        performance.mark('animation-start');
        
        animationStartedRef.current = true;
        
        // Find tiles that are being removed (explosion targets) from selected path
        const removing = new Set<string>();
        selectedPathForAnimation.forEach(pos => {
          removing.add(`${pos.row}-${pos.col}`);
        });
        
        // CRITICAL: Set explosion key BEFORE setting removing tiles
        // This ensures the key is available when tiles calculate their stableKey
        explosionStartTimeRef.current = Date.now();
        explosionKeyRef.current = `explosion-${Date.now()}`;
        
        // CRITICAL: Defer state updates to next frame to allow current frame to complete
        // This prevents the 2-second freeze by allowing React to process the update incrementally
        // Use requestAnimationFrame to ensure animations start on the next frame
        requestAnimationFrame(() => {
          // Batch state updates - React will batch these into a single re-render
          // This reduces the number of renders when starting animations
          // Only the tiles in 'removing' Set will get new keys, preventing remounting ALL tiles
          setRemovingTiles(removing);
          setAnimationPhase('removing');
        });
        
        // DON'T clear removingTiles until after all animations complete
        // Keep them visible during falling animation so tiles don't disappear prematurely
        // Total time: 2300ms (falling starts) + 500ms (falling duration) = 2800ms
        // But clear them slightly before App.tsx cleanup (at 2800ms) to prevent incorrect removals
        setTimeout(() => {
          // Wait for falling to complete, then clear removing tiles
          setTimeout(() => {
            setRemovingTiles(new Set());
            explosionStartTimeRef.current = null;
          }, 500); // Wait for falling animation to complete (500ms)
        }, 2750); // Clear 50ms before App cleanup to prevent grid switch issues
      }
    } else if (!isGravityActive) {
      // When gravity is deactivated, immediately reset everything
      // This prevents any new animations from starting and clears removingTiles
      // to prevent incorrect tile removals when grid switches
      shouldContinueCalculationRef.current = false; // Cancel any ongoing calculations
      animationStartedRef.current = false;
      setAnimationPhase('idle');
      setRemovingTiles(new Set()); // Clear immediately to prevent incorrect removals
      setTilePositions(new Map()); // CRITICAL: Clear tile positions to prevent isShifting=true after cleanup
      explosionStartTimeRef.current = null;
      // Clear overlay state
      fallingOverlaysDataRef.current = [];
      fallingOverlaysKeyRef.current = '';
      overlayJSXRef.current = null;
      overlayJSXKeyRef.current = '';
      setFallingOverlaysKey('');
      // Clear origin positions map
      originPositionsMapRef.current = new Map();
    }
  }, [isGravityActive, selectedPathForAnimation, animationPhase]);
  
  // Separate effect for falling calculation - runs when previousGrid becomes available
  
  useEffect(() => {
    // CRITICAL: Time-slice falling tile calculation to avoid blocking UI thread
    // Process columns in batches and yield to browser between batches to prevent freezes
    if (isGravityActive && previousGrid && previousGrid.length > 0 && grid && grid.length > 0) {
      // Reset cancellation flag when starting new calculation
      shouldContinueCalculationRef.current = true;
      
      // Defer calculation to next frame to prevent blocking animation start
      requestAnimationFrame(() => {
        // Check if we should still continue (guard against cancellation)
        if (!shouldContinueCalculationRef.current || !isGravityActive) {
          return;
        }
        
        const positionMap = new Map<string, { oldRow: number; newRow: number; col: number }>();
        const currentGrid = grid;
        const totalCols = previousGrid[0]?.length || 0;
        
        // Process columns in batches to avoid blocking main thread
        // Use time-slicing: process a few columns, then yield to browser
        const COLUMNS_PER_BATCH = 3; // Process 3 columns at a time
        let currentCol = 0;
        
        const processBatch = () => {
          // Check if calculation was cancelled before processing each batch
          if (!shouldContinueCalculationRef.current) {
            return;
          }
          
          const endCol = Math.min(currentCol + COLUMNS_PER_BATCH, totalCols);
          
          // Process this batch of columns
          for (let col = currentCol; col < endCol; col++) {
            // Collect all unused tiles from this column in old grid (top to bottom)
            const oldColumnTiles: Array<{ row: number; letter: Letter }> = [];
            for (let row = 0; row < previousGrid.length; row++) {
              const prevLetter = previousGrid[row]?.[col];
              if (prevLetter && !prevLetter.used && prevLetter.char !== ' ') {
                oldColumnTiles.push({ row, letter: prevLetter });
              }
            }
            
            // Collect all unused tiles from this column in new grid (top to bottom, same order as old)
            // Gravity maintains the relative order, just moves tiles down
            const newColumnTiles: Array<{ row: number; letter: Letter }> = [];
            for (let row = 0; row < currentGrid.length; row++) {
              const currLetter = currentGrid[row]?.[col];
              if (currLetter && !currLetter.used && currLetter.char !== ' ') {
                newColumnTiles.push({ row, letter: currLetter });
              }
            }
            
            // Match tiles by their order: first tile in old matches first tile in new, etc.
            // Gravity maintains order, so oldColumnTiles[0] should match newColumnTiles[0]
            const matches = Math.min(oldColumnTiles.length, newColumnTiles.length);
            for (let i = 0; i < matches; i++) {
              const oldTile = oldColumnTiles[i];
              const newTile = newColumnTiles[i]; // Same index = same order
              
              // Only animate tiles that moved DOWN (gravity only moves down)
              // KEY: Use NEW position as key since we're rendering the new grid
              if (oldTile.row < newTile.row) {
                // This tile moved DOWN - track the movement
                // Use new position as key for lookup during animation (since we render new grid)
                const tileKeyNew = `${newTile.row}-${col}`;
                positionMap.set(tileKeyNew, { 
                  oldRow: oldTile.row, 
                  newRow: newTile.row, 
                  col 
                });
              }
            }
          }
          
          currentCol = endCol;
          
          // If there are more columns to process, yield to browser and continue
          if (currentCol < totalCols) {
            // Use setTimeout with 0 delay to yield to browser, allowing animations to continue
            setTimeout(processBatch, 0);
          } else {
            // All columns processed - now update state and calculate overlays
            // This final work is lightweight compared to the column processing
            setTilePositions(positionMap);
            
            // OPTIMIZE: Build reverse lookup map for origin positions (oldRow-col -> position)
            // This allows O(1) lookup during render instead of O(n) iteration
            const originMap = new Map<string, { oldRow: number; newRow: number; col: number }>();
            positionMap.forEach((pos, key) => {
              if (pos.newRow > pos.oldRow) {
                const originKey = `${pos.oldRow}-${pos.col}`;
                originMap.set(originKey, pos);
              }
            });
            originPositionsMapRef.current = originMap;
            
            // Also extract and store the grid tiles we'll need for overlays
            // This creates a stable reference that won't change when grid updates
            const overlayTilesMap = new Map<string, Letter>();
            positionMap.forEach((pos, key) => {
              if (pos.newRow > pos.oldRow) {
                const tile = grid[pos.newRow]?.[pos.col];
                if (tile && tile.char !== ' ') {
                  overlayTilesMap.set(key, tile);
                }
              }
            });
            // Store in a ref to keep it stable across renders
            overlayTilesRef.current = overlayTilesMap;
            
            // Pre-calculate overlay data to avoid expensive operations during render
            // This prevents frame drops when rendering
            if (positionMap.size > 0) {
              const overlaysKey = Array.from(positionMap.keys()).sort().join('-');
              const overlayData: Array<{
                key: string;
                tile: Letter;
                oldRow: number;
                newRow: number;
                col: number;
                fallDistance: number;
                initialYOffset: number;
                finalTop: number;
                finalLeft: number;
              }> = [];
              
              // Build overlay data array efficiently (done once, not during render)
              positionMap.forEach((pos, key) => {
                if (pos.newRow > pos.oldRow) {
                  const tile = overlayTilesMap.get(key);
                  if (tile && tile.char !== ' ') {
                    const rowDistance = pos.newRow - pos.oldRow;
                    const fallDistance = rowDistance * 65;
                    // Calculate positions: each row is 60px tile + 5px gap = 65px
                    const finalTop = pos.newRow * 65;
                    const finalLeft = pos.col * 65;
                    const initialTop = pos.oldRow * 65;
                    const initialYOffset = initialTop - finalTop; // Negative value (tile starts above)
                    const overlayKey = `falling-${pos.newRow}-${pos.col}`;
                    
                    overlayData.push({
                      key: overlayKey,
                      tile,
                      oldRow: pos.oldRow,
                      newRow: pos.newRow,
                      col: pos.col,
                      fallDistance,
                      initialYOffset,
                      finalTop,
                      finalLeft
                    });
                  }
                }
              });
              
              fallingOverlaysDataRef.current = overlayData;
              fallingOverlaysKeyRef.current = overlaysKey;
            } else {
              fallingOverlaysDataRef.current = [];
              fallingOverlaysKeyRef.current = '';
            }
          }
        };
        
        // Start processing batches
        processBatch();
      });
    }
    // Calculate falling tiles as soon as data is available (not dependent on animationPhase)
    // This ensures data is ready before isShrinking triggers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousGrid, isGravityActive, grid]);
  
  // Effect to handle falling animation when isShrinking becomes true
  useEffect(() => {
    // CRITICAL: Start falling animation phase IMMEDIATELY when isShrinking becomes true
    // Don't wait for animationPhase check - if we're shrinking, we should be falling
    if (isShrinking && animationPhase === 'removing') {
      // Check if we have falling data ready (either in state or ref)
      // Wait for calculation to complete if needed - use a small delay to allow state to update
      const checkAndStartFalling = () => {
        // Check both state and ref - state might not be updated yet but ref will have data
        const hasTilesToFall = tilePositions.size > 0 || fallingOverlaysDataRef.current.length > 0;
        // If calculation hasn't completed yet, wait a bit and check again
        // Use a maximum retry count to prevent infinite loops
        if (!hasTilesToFall) {
          // Check if we're still in the right state before retrying
          if (isShrinking && animationPhase === 'removing' && isGravityActive) {
            setTimeout(checkAndStartFalling, 50); // Check again in 50ms
          }
          return;
        }
        
        if (hasTilesToFall) {
          // There are tiles to fall - start shifting phase IMMEDIATELY
          // NOTE: removingTiles are still set - we keep them visible during falling
          setAnimationPhase('shifting');

           // Complete after falling animation finishes (800ms duration to match animation)
           setTimeout(() => {
             setAnimationPhase('complete');
             // Clear origin map and tile positions immediately when complete
             // This prevents stale state from affecting the grid after animation
             setTilePositions(new Map());
             originPositionsMapRef.current = new Map();
             // Reset to idle after a short delay to ensure tiles become selectable
             setTimeout(() => {
               if (!isGravityActive && !isShrinking) {
                 setAnimationPhase('idle');
               }
             }, 50);
           }, 800);
        }
      };
      
      // Run immediately - don't wait
      checkAndStartFalling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShrinking, animationPhase, isGravityActive]);
  
  // Track the overlay key in state so useMemo can react to it
  const [fallingOverlaysKey, setFallingOverlaysKey] = useState<string>('');
  
  // Sync fallingOverlaysKey state with ref to trigger useMemo
  useEffect(() => {
    if (animationPhase === 'shifting' && fallingOverlaysKeyRef.current) {
      // Update state if ref has a different value
      setFallingOverlaysKey(prev => {
        if (prev !== fallingOverlaysKeyRef.current) {
          return fallingOverlaysKeyRef.current;
        }
        return prev;
      });
    } else if (animationPhase === 'idle' || animationPhase === 'complete') {
      // Clear key when animation phases are done and back to idle
      // This ensures selection highlighting works correctly after animations
      if (fallingOverlaysKey !== '') {
        setFallingOverlaysKey('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationPhase]); // Only depend on animationPhase to avoid infinite loops
  
  // Show old grid whenever isGravityActive is true - simple and reliable
  // This ensures the old grid stays visible throughout the entire animation sequence:
  // 1. Removing phase (explosion) - tiles are visible to explode
  // 2. Shifting phase (falling) - falling tiles can be rendered as overlays
  // 3. Complete phase - keep old grid until App.tsx sets isGravityActive to false
  // Only switch to new grid when isGravityActive becomes false (after all animations complete)
  // This prevents any race conditions or timing issues and ensures explosions are visible
  // CRITICAL: Keep old grid visible during active animation phases only
  // Allow selection when animation phase is 'idle' even if isGravityActive is still true (cleanup pending)
  // IMPORTANT: During 'complete' phase, we should show the new grid to prevent tiles from being incorrectly hidden
  const shouldShowOldGrid = isGravityActive && 
    previousGrid && 
    previousGrid.length > 0 && 
    (animationPhase === 'removing' || animationPhase === 'shifting');
  const displayGrid = shouldShowOldGrid ? previousGrid : grid;
  
  // Store overlay JSX in a ref - created once per key and kept stable
  const overlayJSXRef = React.useRef<React.ReactNode>(null);
  const overlayJSXKeyRef = React.useRef<string>('');
  
  // Create overlays JSX immediately when shifting phase starts - data is pre-calculated
  useEffect(() => {
    // CRITICAL: Create overlays as soon as shifting phase starts AND data is ready
    // Data should already be pre-calculated during removing phase, so this is instant
    if (animationPhase === 'shifting' && fallingOverlaysKeyRef.current && fallingOverlaysDataRef.current.length > 0) {
      // Only create if key changed - prevent double creation
      if (overlayJSXKeyRef.current !== fallingOverlaysKeyRef.current) {
        // Create overlays immediately - no delay
        overlayJSXRef.current = (
          <>
            {fallingOverlaysDataRef.current.map((overlayData) => (
              <FallingOverlayTile key={overlayData.key} overlayData={overlayData} />
            ))}
          </>
        );
        overlayJSXKeyRef.current = fallingOverlaysKeyRef.current;
      }
    } else if (animationPhase === 'idle') {
      // Only clear when animation is completely idle (not during removing, shifting, or complete phases)
      // Keep overlays visible during 'complete' phase to ensure smooth transition
      overlayJSXRef.current = null;
      overlayJSXKeyRef.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationPhase, fallingOverlaysKey]); // Depend on fallingOverlaysKey to trigger when state syncs
  
  // Get JSX from ref - this ensures it's stable across renders
  // Keep overlays visible during 'shifting' and 'complete' phases to prevent premature cleanup
  const fallingOverlaysJSX = (
    isGravityActive && 
    previousGrid && 
    previousGrid.length > 0 && 
    (animationPhase === 'shifting' || animationPhase === 'complete') && 
    overlayJSXRef.current
  ) 
    ? overlayJSXRef.current 
    : null;
  
  const displayGridSize = grid.length;

  // Calculate grid width - use actual grid size to prevent shifting
  // Formula: (tile width * grid size) + (gap * (grid size - 1))
  // For 60px tiles with 5px gaps: (60 * size) + (5 * (size - 1))
  const actualGridSize = grid.length;
  const tileWidth = 60; // Base tile width in pixels
  const gapSize = 5; // Gap between tiles in pixels
  const gridWidth = actualGridSize > 0 
    ? `${(tileWidth * actualGridSize) + (gapSize * (actualGridSize - 1))}px` 
    : 'auto';
  
  return (
    <div 
      className={`game-grid size-${displayGridSize} ${isGravityActive ? 'gravity-active' : ''} ${isShrinking ? 'shrinking' : ''}`} 
      style={{ 
        position: 'relative',
        // Lock grid dimensions ABSOLUTELY to prevent any layout shifts when switching grids
        width: gridWidth,
        minWidth: gridWidth,
        // Allow grid to be wider than container if needed (for 7x7 grids)
        maxWidth: gridWidth,
        height: gridWidth, // Lock height too to prevent vertical shifts
        // Lock grid structure completely
        boxSizing: 'border-box',
        overflow: 'visible',
        // Prevent any transforms that might cause jitter
        transform: 'translateZ(0)', // Force GPU acceleration and prevent reflows
        // Prevent flash during animations
        opacity: 1,
        // Don't use willChange: contents - it causes layout recalculation
        willChange: 'auto',
      }}
    >
      {/* Render grid structure - always use new grid */}
      {displayGrid.map((row, rowIndex) => (
        <div 
          key={rowIndex} 
          className="grid-row" 
          style={{ 
            width: '100%',
            display: 'flex',
            gap: '5px',
            marginBottom: '5px',
            position: 'relative',
            // Lock row structure to prevent column shifting
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            // Optimize rendering during animations - disable containment to prevent layout thrashing
            contain: isGravityActive ? 'none' : 'layout style',
          }}
        >
          {row.map((letter, colIndex) => {
            const tileKey = `${rowIndex}-${colIndex}`;
            
            // Check if this tile should be removed (explosion) - use ORIGINAL letter
            // This prevents empty spaces from being marked as removing
            // Must be calculated BEFORE displayLetter since we need the original letter
            // CRITICAL: Only show explosions during 'removing' phase, but keep tiles hidden during 'shifting'
            // Once falling starts, explosion animations should be complete, but tiles should still be hidden
            const isRemoving = animationPhase === 'removing' && 
              removingTiles.has(tileKey) && 
              letter.char !== ' ' && 
              letter.char !== '' &&
              letter.char !== undefined;
            
            // Hide exploded tiles during falling animation - they should not reappear
            // CRITICAL: Only hide during 'shifting' phase when showing old grid
            // During 'complete' phase, tiles should be at their final positions and visible
            const shouldHideExplodedTile = animationPhase === 'shifting' &&
              removingTiles.has(tileKey) &&
              shouldShowOldGrid &&
              letter.char !== ' ' &&
              letter.char !== '' &&
              letter.char !== undefined;
            
            // CRITICAL: Only change key for tiles that are actually exploding
            // This prevents remounting ALL tiles - only exploding tiles remount
            // Use the tile's position in removingTiles to create unique key for only those tiles
            const isExploding = isRemoving;
            const stableKey = isExploding
              ? `exploding-${rowIndex}-${colIndex}-${letter.char}-${explosionKeyRef.current || 'default'}`
              : `${rowIndex}-${colIndex}-${letter.char}`;
            
            // For falling: check if this tile needs to animate
            // When showing old grid, falling tiles are rendered as overlays
            // When showing new grid, tiles render in place and animate down
            let isShifting = false;
            let shouldHideTile = false;
            // Only hide/show empty tiles during active falling animation
            // After animation completes, show the actual tile at destination
            // CRITICAL: Only check for falling if isGravityActive is true to prevent stale state
            const isFallingActive = isGravityActive && (animationPhase === 'shifting' || animationPhase === 'complete');
            if (isFallingActive && tilePositions.size > 0) {
              // OPTIMIZE: Use direct Map lookup instead of Array.from().find()
              // This is O(1) instead of O(n) for each tile
              const tileKey = `${rowIndex}-${colIndex}`;
              const position = tilePositions.get(tileKey);
              const fallingToThisPosition = position && position.newRow === rowIndex && position.col === colIndex && position.newRow > position.oldRow ? position : null;
              
              // Check if a tile is falling FROM this position (origin)
              // OPTIMIZE: Use reverse lookup map for O(1) lookup instead of O(n) iteration
              const originKey = `${rowIndex}-${colIndex}`;
              const fallingFromThisPosition = originPositionsMapRef.current.get(originKey) || null;
              
              
              if (shouldShowOldGrid) {
                // Showing old grid - overlays will render falling tiles
                // Only hide destination tiles DURING falling (shifting phase), not after completion
                if (fallingToThisPosition && animationPhase === 'shifting') {
                  shouldHideTile = true;
                  isShifting = false;
                }
                // At origin positions, show empty tile to indicate tile has moved
                // We'll render an empty tile instead of the original letter
                if (fallingFromThisPosition) {
                  // Render empty tile - handled below
                  isShifting = false;
                }
              } else {
                // Showing new grid - tiles animate in place
                if (fallingToThisPosition) {
                  // This is the falling tile at its destination - it should animate
                  isShifting = true;
                  shouldHideTile = false;
                }
              }
            }
            
            // OPTIMIZE: Skip selection/availability checks during animations to reduce render cost
            // These checks are expensive and not needed during animations
            // Allow interaction when animations are idle OR complete (but not during removing/shifting)
            // CRITICAL: Also allow hints when isGravityActive is false (animations fully complete)
            const canInteract = (!isGravityActive || animationPhase === 'idle' || animationPhase === 'complete') && !shouldShowOldGrid;
            
            // Show selection when game is idle (not during animations)
            // CRITICAL: Allow selection when animation phase is idle or complete, regardless of isGravityActive flag
            // The shouldShowOldGrid check ensures we're showing the current grid
            // OPTIMIZE: Use Set lookup instead of array.some() for O(1) instead of O(n)
            const isSelected = canInteract && selectedPathSet.has(tileKey);
            const isInPath = isSelected;
            
            // Check if this letter is available for selection
            // CRITICAL: Only show available highlighting AFTER first tile is clicked (selectedPath.length > 0)
            // IMPORTANT: Always use the CURRENT grid for available moves check, not displayGrid
            // OPTIMIZE: Skip this expensive check during animations
            let isAvailable = false;
            if (canInteract && !letter.used && selectedPath.length > 0) {
              // Quick adjacency check to avoid calling canSelectPosition for every tile
              const last = selectedPath[selectedPath.length - 1];
              const dr = Math.abs(last.row - rowIndex);
              const dc = Math.abs(last.col - colIndex);
              if ((dr === 0 && dc === 0) || dr > 1 || dc > 1) {
                isAvailable = false;
              } else {
                // Delegate final validation (bounds/dup) to canSelectPosition only for close tiles
                // Use currentGridForMoves to check against the actual current grid state
                isAvailable = canSelectPosition({ row: rowIndex, col: colIndex }, selectedPath, grid);
              }
            }
            
            // Allow hints to show when not showing old grid (hints work on current grid state)
            // Hints should always work when we're showing the current grid, regardless of animation state
            const canShowHints = !shouldShowOldGrid;
            
            // Debug logging for hint highlighting - log for ALL tiles when hint exists
            const isHintedStart = canShowHints && !!gameState.hint && gameState.hint.type === 'firstLetter' && gameState.hint.position.row === rowIndex && gameState.hint.position.col === colIndex;
            const isHintedNext = canShowHints && !!gameState.hint && gameState.hint.type === 'nextStep' && gameState.hint.position.row === rowIndex && gameState.hint.position.col === colIndex;
            
            // Log when hint exists and we're checking tiles
            
            // Calculate initial Y offset for falling animation
            // Note: Falling tiles are rendered as overlays, so main grid tiles don't need this
            // Overlays handle all the falling animation logic
            let initialYOffset = 0;
            
            // Hide tiles at destination positions during falling to prevent overlap
            // The falling tile will animate to this position
            if (shouldHideTile && !isShifting) {
              return (
                <div 
                  key={stableKey}
                  style={{ 
                    width: '60px',
                    height: '60px',
                    flexShrink: 0,
                    minWidth: '60px',
                    maxWidth: '60px',
                  }}
                />
              );
            }
            
            // Check if we should show empty tile at origin position of falling tile
            // OPTIMIZE: Use reverse lookup map for O(1) lookup instead of O(n) iteration
            // CRITICAL: Only hide origin tiles during 'shifting' phase, not 'complete'
            // During 'complete', tiles should be at their final positions already
            const originKey = `${rowIndex}-${colIndex}`;
            const isOriginPosition = originPositionsMapRef.current.has(originKey);
            const shouldShowEmptyAtOrigin = shouldShowOldGrid && 
              isOriginPosition && 
              animationPhase === 'shifting'; // Only hide during shifting, not complete
            
            
            // Use empty letter if showing empty tile at origin or if tile was exploded
            // IMPORTANT: Don't change the letter for isRemoving check - use original letter
            // CRITICAL: Only hide tiles when showing OLD grid - new grid already has tiles removed
            const displayLetter = (shouldShowEmptyAtOrigin || shouldHideExplodedTile)
              ? { ...letter, char: ' ', used: true }
              : ((isShifting && removingTiles.size > 0 && displayGrid === previousGrid && grid[rowIndex]?.[colIndex])
                  ? grid[rowIndex][colIndex]
                  : letter);
            
            return (
              <div 
                key={stableKey} 
                style={{ 
                  position: 'relative',
                  // Fixed dimensions to prevent layout shifts
                  width: '60px',
                  height: '60px',
                  flexShrink: 0,
                  // Lock horizontal position absolutely
                  minWidth: '60px',
                  maxWidth: '60px',
                  // Optimize for smooth animations
                  willChange: 'auto', // Main grid tiles don't animate
                  overflow: 'visible',
                  // Optimize rendering
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  // Only contain layout for non-animating tiles
                  contain: (!isRemoving && !isShifting) ? 'layout style paint' : 'none',
                }}
              >
                {IS_DEBUG && (isRemoving || isShifting) && (
                  <div style={{ position: 'absolute', top: 0, left: 0, fontSize: '8px', background: 'rgba(255,0,0,0.5)', zIndex: 9999 }}>
                    {isRemoving ? 'R' : ''}{isShifting ? 'S' : ''}
                  </div>
                )}
                <LetterTile
                  key={stableKey}
                  letter={displayLetter}
                  isSelected={isSelected}
                  isInPath={isInPath}
                  isAvailable={isAvailable}
                  onClick={onLetterClick}
                  isRemoving={isRemoving && displayLetter.char !== ' '}
                  isShifting={isShifting}
                  // DEBUG: Add a key that includes selection state to force re-render
                  // This helps debug if memo is preventing updates
                  isHinted={isHintedStart || isHintedNext}
                  isHintedNext={isHintedNext}
                  fallDistance={Math.abs(initialYOffset)}
                  initialYOffset={initialYOffset}
                  isValidatingWord={isValidatingWord && isSelected}
                  wordValidationResult={isSelected ? wordValidationResult : null}
                />
              </div>
            );
          })}
        </div>
      ))}
      
      {/* Render falling tiles as overlays when showing old grid during shifting phase */}
      {/* This prevents layout shifts and allows explosions to complete while tiles fall */}
      {/* CRITICAL: Overlay data is pre-calculated in useEffect to avoid expensive operations during render */}
      
      {/* Render memoized falling overlays - only show if we should show old grid */}
      {fallingOverlaysJSX}
      
    </div>
  );
  }, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders during animations
  // During animations, only re-render if critical animation props change
  // CRITICAL: Don't check selectedPath during animations - it will reset animation state
  const isAnimating = prevProps.isGravityActive || nextProps.isGravityActive;
  
  // During animations, only re-render if critical props change
  // OPTIMIZE: Use fast reference comparisons only - avoid deep equality checks
  // CRITICAL: Ignore selectedPath changes during animations to prevent resetting explosion state
  if (isAnimating) {
    // During animations, completely ignore selectedPath changes to prevent re-renders
    return (
      prevProps.isGravityActive === nextProps.isGravityActive &&
      prevProps.isShrinking === nextProps.isShrinking &&
      prevProps.previousGrid === nextProps.previousGrid &&
      prevProps.selectedPathForAnimation === nextProps.selectedPathForAnimation
    );
  }
  
  // When not animating, use reference equality for selectedPath (reducer always creates new array)
  // This is fast and doesn't block the render thread
  // CRITICAL: Check hint deeply - the reducer creates a new hint object, but we need to compare values
  const hintChanged = (
    prevProps.gameState.hint?.type !== nextProps.gameState.hint?.type ||
    prevProps.gameState.hint?.position.row !== nextProps.gameState.hint?.position.row ||
    prevProps.gameState.hint?.position.col !== nextProps.gameState.hint?.position.col ||
    (prevProps.gameState.hint === null) !== (nextProps.gameState.hint === null)
  );
  
  const shouldSkipRender = (
    prevProps.isGravityActive === nextProps.isGravityActive &&
    prevProps.isShrinking === nextProps.isShrinking &&
    prevProps.isValidatingWord === nextProps.isValidatingWord &&
    prevProps.wordValidationResult === nextProps.wordValidationResult &&
    prevProps.selectedPathForAnimation === nextProps.selectedPathForAnimation &&
    prevProps.previousGrid === nextProps.previousGrid &&
    prevProps.gameState.grid === nextProps.gameState.grid &&
    prevProps.gameState.selectedPath === nextProps.gameState.selectedPath &&
    !hintChanged && // Re-render if hint changed
    prevProps.onLetterClick === nextProps.onLetterClick
  );
  
  return shouldSkipRender;
});

GameGrid.displayName = 'GameGrid';

export default GameGrid;
